import React from "react";
import {
    Box,
    makeStyles,
    Typography,
    TextField,
    Select,
    MenuItem,
    FormControl,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    CircularProgress,
} from "@material-ui/core";
import GetAppIcon from "@material-ui/icons/GetApp";
import { TablePaginationActions } from "components/tablePagination";
import DateRangePicker from "components/input/dateRangePicker";

const useStyles = makeStyles((theme) => ({
    pageContainer: {
        backgroundColor: "#FFFFFF",
        borderRadius: "12px",
        border: "1px solid #E4E7EC",
        margin: "16px 24px",
        overflow: "hidden",
    },
    header: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "20px 24px",
        borderBottom: "1px solid #E4E7EC",
    },
    title: {
        fontSize: "18px",
        fontWeight: 600,
        color: "#101828",
    },
    searchFieldFull: {
        minWidth: "160px",
        "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
            backgroundColor: "#FFFFFF",
            height: "40px",
            "& fieldset": {
                borderColor: "#D0D5DD",
            },
        },
        "& .MuiOutlinedInput-input": {
            padding: "10px 14px",
            fontSize: "14px",
        },
    },
    filtersContainer: {
        display: "flex",
        gap: "16px",
        padding: "16px 24px",
        backgroundColor: "#F9FAFB",
        borderBottom: "1px solid #E4E7EC",
        flexWrap: "wrap",
        alignItems: "flex-end",
    },
    filterGroup: {
        display: "flex",
        flexDirection: "column",
        gap: "6px",
    },
    filterLabel: {
        fontSize: "12px",
        fontWeight: 500,
        color: "#344054",
    },
    filterSelect: {
        minWidth: "160px",
        "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
            backgroundColor: "#FFFFFF",
            height: "40px",
        },
        "& .MuiSelect-select": {
            padding: "10px 14px",
            fontSize: "14px",
        },
    },
    dateRangePicker: {
        "& #date-range-picker": {
            "& .rs-picker-toggle-wrapper": {
                height: "40px",
            },
            "& .rs-picker-default .rs-picker-toggle.rs-btn": {
                backgroundColor: "#FFFFFF !important",
                borderRadius: "8px !important",
                border: "1px solid #D0D5DD !important",
                height: "40px",
            },
        },
    },
    applyButton: {
        backgroundColor: "#3C4E54",
        color: "#FFFFFF",
        textTransform: "none",
        fontWeight: 500,
        padding: "10px 20px",
        borderRadius: "8px",
        "&:hover": {
            backgroundColor: "#2E3E44",
        },
    },
    exportButton: {
        backgroundColor: "#FFFFFF",
        color: "#344054",
        textTransform: "none",
        fontWeight: 500,
        padding: "10px 16px",
        borderRadius: "8px",
        border: "1px solid #D0D5DD",
        "&:hover": {
            backgroundColor: "#F9FAFB",
        },
    },
    tableContainer: {
        overflowX: "auto",
        "& .MuiTable-root": {
            minWidth: "800px",
        },
        "& .MuiTableCell-head": {
            backgroundColor: "#F9FAFB",
            fontWeight: 600,
            fontSize: "12px",
            color: "#667085",
            textTransform: "uppercase",
            padding: "12px 16px",
            borderBottom: "1px solid #E4E7EC",
            whiteSpace: "nowrap",
        },
        "& .MuiTableCell-body": {
            fontSize: "14px",
            color: "#344054",
            padding: "12px 16px",
            borderBottom: "1px solid #E4E7EC",
        },
    },
    printJobLink: {
        color: "#1D4179",
        fontWeight: 500,
        cursor: "pointer",
        textDecoration: "none",
        "&:hover": {
            textDecoration: "underline",
        },
    },
    reimbursedValue: {
        color: "#12B76A",
        fontWeight: 500,
    },
    loadingContainer: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "60px 0",
    },
    noDataContainer: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "60px 0",
        color: "#667085",
    },
}));

export default function CreditMovementPage({
    listingData,
    pagination,
    filters,
    filterOptions,
    onPageChange,
    onRowsPerPageChange,
    onFilterChange,
    onApplyFilters,
    onSearchChange,
    onExport,
    onClearFilters,
    translate,
    paginationOptions = [25, 50, 100],
}) {
    const classes = useStyles();

    return (
        <Box className={classes.pageContainer}>
            {/* Header */}
            <Box className={classes.header}>
                <Typography className={classes.title}>
                    {translate("label.CREDIT_MOVEMENT_LISTING")}
                </Typography>
            </Box>

            {/* Filters */}
            <Box className={classes.filtersContainer}>
                {/* Search - First */}
                <Box className={classes.filterGroup}>
                    <Typography className={classes.filterLabel}>{translate("label.PRINT_JOB_ID")}</Typography>
                    <TextField
                        placeholder={translate("label.PRINT_JOB_ID")}
                        variant="outlined"
                        size="small"
                        className={classes.searchFieldFull}
                        value={filters?.searchPrintJobId || ""}
                        onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
                    />
                </Box>

                {/* Product SKU */}
                <Box className={classes.filterGroup}>
                    <Typography className={classes.filterLabel}>{translate("label.PRODUCT_SKU")}</Typography>
                    <FormControl variant="outlined" size="small" className={classes.filterSelect}>
                        <Select
                            value={filters?.productSku || "all"}
                            onChange={(e) => onFilterChange && onFilterChange({ productSku: e.target.value })}
                        >
                            <MenuItem value="all">{translate("label.ALL_PRODUCTS")}</MenuItem>
                            {filterOptions?.products?.map((product) => (
                                <MenuItem key={product.value} value={product.value}>
                                    {product.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {/* Date Range - Last before buttons */}
                <Box className={classes.filterGroup}>
                    <Typography className={classes.filterLabel}>{translate("label.DATE_RANGE")}</Typography>
                    <Box className={classes.dateRangePicker}>
                        <DateRangePicker
                            placeholder={translate("placeholder.SELECT_DATE_RANGE")}
                            value={filters?.dateRange}
                            handleChange={(v) => onFilterChange && onFilterChange({ dateRange: v })}
                        />
                    </Box>
                </Box>

                {/* Spacer to push buttons to right */}
                <Box style={{ flex: 1 }} />

                {/* Apply Filters + Clear + Export - Right side */}
                <Button
                    variant="contained"
                    className={classes.applyButton}
                    onClick={onApplyFilters}
                >
                    {translate("label.APPLY_FILTERS")}
                </Button>

                <Button
                    variant="outlined"
                    className={classes.exportButton}
                    onClick={onClearFilters}
                >
                    {translate("label.CLEAR")}
                </Button>

                <Button
                    variant="outlined"
                    className={classes.exportButton}
                    startIcon={<GetAppIcon />}
                    onClick={onExport}
                >
                    {translate("label.EXPORT")}
                </Button>
            </Box>

            {/* Table */}
            {listingData?.isLoading ? (
                <Box className={classes.loadingContainer}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer className={classes.tableContainer}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{translate("label.PRINT_JOB_ID")}</TableCell>
                                <TableCell>{translate("label.PRODUCT_SKU")}</TableCell>
                                <TableCell>{translate("label.START_DATE")}</TableCell>
                                <TableCell>{translate("label.COMPLETION")}</TableCell>
                                <TableCell align="right">{translate("label.REQUESTED")}</TableCell>
                                <TableCell align="right">{translate("label.PRINTED")}</TableCell>
                                <TableCell align="right">{translate("label.REIMBURSED")}</TableCell>
                                <TableCell align="right">{translate("label.BALANCE_AFTER")}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {listingData?.data?.length > 0 ? (
                                listingData.data.map((row) => (
                                    <TableRow key={row.printJobId} hover>
                                        <TableCell>
                                            <span className={classes.printJobLink}>{row.printJobId}</span>
                                        </TableCell>
                                        <TableCell>{row.productSku}</TableCell>
                                        <TableCell>{row.startDateTime}</TableCell>
                                        <TableCell>{row.completionDateTime}</TableCell>
                                        <TableCell align="right">{row.requested?.toLocaleString()}</TableCell>
                                        <TableCell align="right">{row.printed?.toLocaleString()}</TableCell>
                                        <TableCell align="right" className={classes.reimbursedValue}>
                                            {row.reimbursed?.toLocaleString()}
                                        </TableCell>
                                        <TableCell align="right">{row.balanceAfter?.toLocaleString()}</TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        <Box className={classes.noDataContainer}>
                                            {translate("label.NO_DATA_AVAILABLE")}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Pagination */}
            <TablePagination
                ActionsComponent={TablePaginationActions}
                rowsPerPageOptions={paginationOptions}
                component="div"
                count={listingData?.totalRecords || 0}
                rowsPerPage={pagination?.rowsPerPage || 25}
                page={pagination?.page || 0}
                onChangePage={(e, newPage) => onPageChange && onPageChange(newPage)}
                onChangeRowsPerPage={(e) => onRowsPerPageChange && onRowsPerPageChange(parseInt(e.target.value, 10))}
            />
        </Box>
    );
}
